import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import {
  EnvelopeOpen, ArrowLeft, ArrowsClockwise, Eye, PaperPlaneTilt,
  ArrowCounterClockwise, FloppyDisk, CheckCircle, Warning, Plus,
} from "@phosphor-icons/react";

function PencilHint() {
  return (
    <span className="text-[10px] uppercase tracking-wider text-ms-warn ml-2">customized</span>
  );
}

// ---------- List ----------
export default function AdminEmailTemplates() {
  const { key } = useParams();
  if (key) return <TemplateEditor templateKey={key} />;
  return <TemplatesIndex />;
}

function TemplatesIndex() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchTo, setBatchTo] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  useEffect(() => {
    api.get("/admin/email-templates").then(r => {
      setRows(r.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const sendAllPreviews = async () => {
    if (!window.confirm(`Send a preview of all ${rows.length} email templates to ${batchTo || "your admin email"}? Use this to print/proofread the whole library at once. No real flows are triggered.`)) return;
    setBatchBusy(true);
    setBatchResult(null);
    try {
      const r = await api.post("/admin/email-templates/send-all-previews", { to: batchTo.trim() });
      setBatchResult(r.data);
    } catch (e) {
      setBatchResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setBatchBusy(false);
    }
  };

  return (
    <div className="p-5 md:p-12" data-testid="admin-email-templates">
      <p className="label-eyebrow text-ms-stone">System</p>
      <h1 className="serif text-3xl md:text-4xl text-ms-earth mt-2">Email Templates</h1>
      <p className="mt-2 text-sm text-ms-stone max-w-2xl">
        Edit the copy of every email Mystery Served sends automatically. The brand wrapper
        (header, dividers, footer) stays consistent — you control subject, eyebrow, heading,
        and the body. Variables like <code className="bg-ms-cream px-1 rounded">{"{first_name}"}</code> are
        replaced per-recipient. Each template has a "Reset to default" if you want to start over.
      </p>

      {/* Batch preview block — send all templates to one inbox for proofreading */}
      <div className="mt-6 bg-ms-parchment border border-ms-edge rounded-md p-5 max-w-2xl" data-testid="batch-preview-block">
        <p className="label-eyebrow text-ms-brass text-[10px]">Print &amp; proofread</p>
        <p className="serif text-ms-earth text-lg mt-1">Send me one of each — proofreading mode.</p>
        <p className="text-ms-stone text-xs mt-1.5 leading-[1.6]">
          Fires every template at sample data so you can compare them side-by-side. Each subject is prefixed
          with <code className="bg-white px-1 rounded">[PREVIEW: name]</code> and the email body opens with a
          banner showing exactly when that template fires. No real bookings, no real flows.
        </p>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={batchTo}
            onChange={e => setBatchTo(e.target.value)}
            placeholder="Send to: (leave blank for admin_notify_email)"
            className="flex-1 px-3 py-2 text-sm border border-ms-edge rounded-md"
            data-testid="batch-preview-to"
          />
          <button
            onClick={sendAllPreviews}
            disabled={batchBusy || loading}
            className="px-5 py-2 bg-ms-earth text-ms-cream rounded-md text-xs label-eyebrow disabled:opacity-50"
            data-testid="batch-preview-send"
          >
            {batchBusy ? "Sending…" : `Send ${rows.length || ""} previews`}
          </button>
        </div>
        {batchResult && (
          <div className="mt-3 text-xs" data-testid="batch-preview-result">
            {batchResult.error ? (
              <p className="text-ms-danger">Couldn&apos;t send: {batchResult.error}</p>
            ) : (
              <p className="text-ms-success">
                ✓ Sent <b>{batchResult.sent}</b> of <b>{batchResult.total}</b> previews to <b>{batchResult.to}</b>.
                Check your inbox (and Spam/All Mail just in case).
              </p>
            )}
          </div>
        )}
      </div>

      <section className="mt-6 md:mt-8">
        {loading ? (
          <p className="p-6 text-sm text-ms-stone">Loading…</p>
        ) : (
          <>
            {/* Mobile: stacked cards */}
            <div className="md:hidden space-y-3" data-testid="tpl-list-mobile">
              {rows.map(r => (
                <Link
                  key={r.key}
                  to={`/admin/email-templates/${r.key}`}
                  className="block bg-white border border-ms-edge rounded-xl p-4 active:bg-ms-cream/40 transition-colors"
                  data-testid={`tpl-row-${r.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-ms-earth font-medium flex items-center gap-2 flex-1 min-w-0">
                      <span className="truncate">{r.name}</span>
                      {r.is_customized && <PencilHint />}
                    </p>
                    <span className="text-xs label-eyebrow text-ms-brass flex-shrink-0" data-testid={`tpl-edit-${r.key}`}>Edit →</span>
                  </div>
                  <p className="text-xs text-ms-stone mt-1.5 line-clamp-2">{r.description}</p>
                  <div className="mt-3 pt-3 border-t border-ms-edge">
                    <p className="text-[10px] uppercase tracking-wider text-ms-stone">Subject</p>
                    <p className="text-sm text-ms-stone mt-0.5 break-words">{r.subject}</p>
                    {r.updated_at && (
                      <p className="text-[11px] text-ms-stone mt-2">Edited {new Date(r.updated_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block bg-white border border-ms-edge rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ms-cream/40 border-b border-ms-edge">
                  <tr>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Template</th>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Subject</th>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Last edited</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key} className="border-b border-ms-edge last:border-0 hover:bg-ms-cream/30">
                      <td className="p-4 align-top">
                        <p className="text-ms-earth font-medium">
                          {r.name}
                          {r.is_customized && <PencilHint />}
                        </p>
                        <p className="text-xs text-ms-stone mt-1 max-w-md">{r.description}</p>
                      </td>
                      <td className="p-4 align-top text-ms-stone">{r.subject}</td>
                      <td className="p-4 align-top text-xs text-ms-stone">
                        {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                      </td>
                      <td className="p-4 align-top text-right">
                        <Link
                          to={`/admin/email-templates/${r.key}`}
                          className="text-xs label-eyebrow text-ms-brass hover:text-ms-brass-lo"
                        >
                          Edit →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

// ---------- Editor ----------
function TemplateEditor({ templateKey }) {
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [draft, setDraft] = useState({ subject: "", eyebrow: "", heading: "", body_markdown: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [defaultRecipient, setDefaultRecipient] = useState("admin");
  const [senderEmail, setSenderEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  // Pull the actual default recipient (admin_notify_email) so the placeholder is truthful.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/settings`);
        if (!cancelled) {
          setDefaultRecipient(r.data.admin_notify_email || r.data.sender_email || "");
          setSenderEmail(r.data.sender_email || "");
        }
      } catch { /* fine to leave placeholder generic */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = () => {
    api.get(`/admin/email-templates/${templateKey}`).then(r => {
      setTpl(r.data);
      setDraft({
        subject: r.data.subject || "",
        eyebrow: r.data.eyebrow || "",
        heading: r.data.heading || "",
        body_markdown: r.data.body_markdown || "",
      });
      setDirty(false);
      setPreviewHtml("");
    });
  };
  useEffect(load, [templateKey]);

  const updateField = (field, value) => {
    setDraft(d => ({ ...d, [field]: value }));
    setDirty(true);
    setSaveResult(null);
  };

  const onPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post(`/admin/email-templates/${templateKey}/preview`, draft);
      setPreviewHtml(r.data.html);
      setPreviewSubject(r.data.subject_rendered);
    } finally {
      setPreviewing(false);
    }
  };

  const onSendTest = async () => {
    setTestResult(null);
    setTestSending(true);
    try {
      const r = await api.post(`/admin/email-templates/${templateKey}/send-test`, {
        ...draft,
        to: testTo.trim() || undefined,
      });
      setTestResult({ ok: true, to: r.data.to, transport: r.data.transport });
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Send failed" });
    } finally {
      setTestSending(false);
    }
  };

  const onSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await api.patch(`/admin/email-templates/${templateKey}`, draft);
      setSaveResult({ ok: true });
      setDirty(false);
      load();
    } catch (e) {
      setSaveResult({ ok: false, error: e?.response?.data?.detail || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("Revert this template to its default copy? Your customizations will be discarded.")) return;
    await api.post(`/admin/email-templates/${templateKey}/reset`);
    load();
  };

  const insertVariable = (varName) => {
    const token = `{${varName}}`;
    // Insert at end of body for simplicity (cursor-aware insertion would need refs)
    updateField("body_markdown", draft.body_markdown + (draft.body_markdown.endsWith("\n") || !draft.body_markdown ? "" : " ") + token);
  };

  if (!tpl) {
    return <div className="p-10 text-sm text-ms-stone">Loading template…</div>;
  }

  return (
    <div className="p-5 md:p-12" data-testid={`tpl-editor-${templateKey}`}>
      <Link to="/admin/email-templates" className="inline-flex items-center gap-2 text-xs label-eyebrow text-ms-stone hover:text-ms-earth" data-testid="tpl-back">
        <ArrowLeft size={12} /> Back to templates
      </Link>
      <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-ms-stone flex items-center gap-2">
            <EnvelopeOpen size={14} /> Email template
            {tpl.is_customized && <span className="text-ms-warn text-[10px]">CUSTOMIZED</span>}
          </p>
          <h1 className="serif text-3xl text-ms-earth mt-2">{tpl.name}</h1>
          <p className="mt-2 text-sm text-ms-stone max-w-2xl">{tpl.description}</p>
        </div>
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-ms-edge rounded-md hover:bg-ms-danger/10 hover:border-ms-danger text-ms-stone hover:text-ms-danger transition-colors"
          data-testid="tpl-reset"
        >
          <ArrowCounterClockwise size={12} /> Reset to default
        </button>
      </div>

      <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Editor — left 2/3 */}
        <div className="lg:col-span-2 space-y-5">
          <section className="bg-white border border-ms-edge rounded-2xl p-4 md:p-6 space-y-4">
            <label className="block">
              <span className="label-eyebrow text-ms-stone text-xs">Subject line</span>
              <input
                value={draft.subject}
                onChange={e => updateField("subject", e.target.value)}
                className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm"
                data-testid="tpl-subject"
              />
              <p className="mt-1 text-xs text-ms-stone">Appears in the inbox subject line. Supports <code>{"{variables}"}</code>.</p>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="label-eyebrow text-ms-stone text-xs">Eyebrow tagline</span>
                <input
                  value={draft.eyebrow}
                  onChange={e => updateField("eyebrow", e.target.value)}
                  className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm"
                  data-testid="tpl-eyebrow"
                />
                <p className="mt-1 text-xs text-ms-stone">Small uppercase line at the top.</p>
              </label>
              <label className="block">
                <span className="label-eyebrow text-ms-stone text-xs">Heading (H1)</span>
                <input
                  value={draft.heading}
                  onChange={e => updateField("heading", e.target.value)}
                  className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm serif"
                  data-testid="tpl-heading"
                />
                <p className="mt-1 text-xs text-ms-stone">The big top-of-email line.</p>
              </label>
            </div>

            <label className="block">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow text-ms-stone text-xs">Body</span>
                <span className="text-xs text-ms-stone">
                  <code className="bg-ms-cream px-1 rounded">**bold**</code> ·
                  <code className="bg-ms-cream px-1 rounded ml-1">[link](https://...)</code> ·
                  blank line = paragraph
                </span>
              </div>
              <textarea
                value={draft.body_markdown}
                onChange={e => updateField("body_markdown", e.target.value)}
                rows={14}
                className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm font-mono"
                data-testid="tpl-body"
              />
            </label>
          </section>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onPreview}
              disabled={previewing || !draft.subject.trim() || !draft.body_markdown.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-ms-earth text-ms-earth rounded-md hover:bg-ms-earth hover:text-ms-cream transition-colors disabled:opacity-40"
              data-testid="tpl-preview-btn"
            >
              <Eye size={14} /> {previewing ? "Rendering…" : "Preview"}
            </button>
            <button
              onClick={onSave}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-ms-earth text-ms-cream rounded-md label-eyebrow text-xs disabled:opacity-40 hover:bg-ms-brass transition-colors"
              data-testid="tpl-save"
            >
              <FloppyDisk size={14} /> {saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
            </button>
            {saveResult && (
              <span className={`text-xs ${saveResult.ok ? "text-ms-success" : "text-ms-danger"}`} data-testid="tpl-save-result">
                {saveResult.ok ? "Saved." : `Failed: ${saveResult.error}`}
              </span>
            )}
          </div>
        </div>

        {/* Variables sidebar — right 1/3 */}
        <aside className="bg-white border border-ms-edge rounded-2xl p-4 md:p-6 h-fit lg:sticky lg:top-6">
          <p className="label-eyebrow text-ms-stone text-xs">Available variables</p>
          <p className="text-xs text-ms-stone mt-1">Click to append to the body.</p>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="tpl-variable-chips">
            {(tpl.variables || []).map(v => (
              <button
                key={v}
                onClick={() => insertVariable(v)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono border border-ms-edge rounded hover:bg-ms-earth hover:text-ms-cream hover:border-ms-earth transition-colors"
                data-testid={`tpl-var-${v}`}
                title={`Sample: ${tpl.sample?.[v] || "—"}`}
              >
                <Plus size={9} /> {`{${v}}`}
              </button>
            ))}
          </div>
          {tpl.show_booking_id && (
            <p className="mt-4 text-[11px] text-ms-stone">
              A Booking ID footer line is automatically appended to this template.
            </p>
          )}

          <div className="mt-6 pt-6 border-t border-ms-edge">
            <p className="label-eyebrow text-ms-stone text-xs">Default copy</p>
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-ms-brass label-eyebrow">View default</summary>
              <div className="mt-3 space-y-2 text-xs text-ms-stone">
                <div><b className="text-ms-earth">Subject:</b> {tpl.default?.subject}</div>
                <div><b className="text-ms-earth">Eyebrow:</b> {tpl.default?.eyebrow}</div>
                <div><b className="text-ms-earth">Heading:</b> {tpl.default?.heading}</div>
                <div className="whitespace-pre-wrap font-mono bg-ms-cream/40 p-2 rounded border border-ms-edge">{tpl.default?.body_markdown}</div>
              </div>
            </details>
          </div>
        </aside>
      </div>

      {/* Preview pane */}
      {previewHtml && (
        <section className="mt-8 bg-white border border-ms-edge rounded-2xl overflow-hidden" data-testid="tpl-preview-pane">
          <div className="flex items-center justify-between p-4 border-b border-ms-edge bg-ms-cream/40">
            <div>
              <p className="label-eyebrow text-ms-stone text-xs">Preview · sample data</p>
              <p className="text-sm text-ms-earth mt-1">Subject: <b>{previewSubject}</b></p>
            </div>
            <div className="flex items-center gap-2">
              <input
                value={testTo}
                onChange={e => setTestTo(e.target.value)}
                placeholder={defaultRecipient ? `defaults to ${defaultRecipient}` : "test@yourdomain.com"}
                className="px-3 py-1.5 text-xs border border-ms-edge rounded-md min-w-[260px]"
                data-testid="tpl-test-to"
              />
              <button
                onClick={onSendTest}
                disabled={testSending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-ms-edge rounded-md hover:bg-white disabled:opacity-40"
                data-testid="tpl-test-btn"
              >
                <PaperPlaneTilt size={12} /> {testSending ? "Sending…" : "Send test to me"}
              </button>
            </div>
          </div>
          {testResult && (
            <div className={`px-4 py-2 text-xs border-b border-ms-edge ${testResult.ok ? "bg-ms-success/10 text-ms-success" : "bg-ms-danger/10 text-ms-danger"}`} data-testid="tpl-test-result">
              {testResult.ok ? (
                <span><CheckCircle size={12} weight="fill" className="inline mr-1" /> Test sent to <b>{testResult.to}</b> via <b>{testResult.transport}</b>.</span>
              ) : (
                <span><Warning size={12} weight="fill" className="inline mr-1" /> {testResult.error}</span>
              )}
              {testResult.ok && senderEmail && testResult.to &&
                  (testResult.to.toLowerCase() === senderEmail.toLowerCase() ||
                   testResult.to.toLowerCase().endsWith("@" + senderEmail.split("@")[1])) && (
                <div className="mt-1.5 text-ms-stone bg-ms-cream/60 px-2 py-1.5 rounded text-[11px]" data-testid="self-loop-warn">
                  <Warning size={11} weight="fill" className="inline mr-1 text-ms-brass" />
                  <b>Heads up:</b> you&apos;re sending from <b>{senderEmail}</b> to <b>{testResult.to}</b> — Gmail often silently drops or spam-files emails between same-domain addresses, especially aliases. If you don&apos;t see it in the inbox, check Spam/All Mail, or try a different test address (Yahoo, Outlook, your phone) to confirm delivery.
                </div>
              )}
            </div>
          )}
          <iframe
            srcDoc={previewHtml}
            title="Template preview"
            className="w-full h-[640px] bg-ms-cream"
            data-testid="tpl-preview-iframe"
          />
        </section>
      )}
    </div>
  );
}
